import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import TabPanel, { a11yProps } from '@/components/tab-panel/tab-panel';
import SystemsBudget from '@/screens/budgets/components/systems-budget';
import DepartmentsBudget from '@/screens/budgets/components/departments-budget';
import { ActionCenterV2 } from '@/components/action-center/action-center-v2';
import { ROUTES, urls } from '@/urls';
import GlobalBudget from '@/screens/budgets/components/global-budget';
import { checkSpecialPermission, hasUserAccessTo } from '@/lib/permissions';
import 'intro.js/introjs.css';
import BudgetChangeApproval from '@/screens/budgets/components/budget-change-approval';
import BudgetStatsRow from '@/screens/budgets/components/budget-stats-row';
import { getToken, getUser } from '@/lib/common';
import { AppContainer } from '@/components/ui';
import { PermissionArea } from '@cloudcost/shared-types/users';
import { useSystems } from '@/screens/budgets/components/hooks/useSystems';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';
import GlobalBudgetSplit from './components/global-budget-split';
import { CurrencyProvider } from '@/contexts/CurrencyContext';
import DepartmentBudgetSplit from './components/department-budget-split';
import { useActionItemDetails } from '@/hooks/useActionItemDetails';
import ActionItemTextDialog from '@/components/action-center/action-item-text-dialog';
import { useActionCenterViewController } from '@/hooks/useActionCenterViewController';
import { createBudgetsActionCenterHandler } from '@/screens/budgets/action-center-view.config';
import { scrollSectionIntoView } from '@/utils/actionCenterFocus';
import { useActionCenterViewContext } from '@/contexts/action-center-view-context';

export interface BudgetTabItem {
  id: string;
  label: string;
  component: React.ReactNode;
  hasAccess: boolean;
  dataIntro: string;
}

function Budgets() {
  const user = getUser();
  const [availableTabs, setAvailableTabs] = useState<BudgetTabItem[]>([]);
  const [hasFetchedTabs, setHasFetchedTabs] = useState(false);
  const [actionCenterItems, setActionCenterItems] = useState<ActionCenterItemV2[]>([]);
  const [selectedActionItemId, setSelectedActionItemId] = useState<string | null>(null);
  const { item: v2Item } = useActionItemDetails('Budgets', selectedActionItemId, actionCenterItems);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [value, setValue] = useState(0);
  const [focusDepartmentId, setFocusDepartmentId] = useState<number | undefined>(undefined);
  const [focusSystemId, setFocusSystemId] = useState<number | undefined>(undefined);
  const { clearSelection, selection } = useActionCenterViewContext();

  // Get systems to check if system exists in owned systems for tab routing
  const canViewSystemList =
    hasUserAccessTo(PermissionArea.SYSTEM_VIEW_ALL) ||
    hasUserAccessTo(PermissionArea.SYSTEM_VIEW_DEPARTMENT) ||
    hasUserAccessTo(PermissionArea.SYSTEM_VIEW_OWNED);

  const activeTabId = availableTabs[value]?.id;
  const isSystemsTabActive = activeTabId === 'systems';
  const isSplitTabActive =
    activeTabId === 'global-budget-split' || activeTabId === 'department-budget-split';
  const selectionIsSystemScope =
    selection?.module === 'Budgets' &&
    ['system', 'systems'].includes(String(selection?.item?.scope ?? '').toLowerCase());

  // Only load /api/systems when the current view actually needs it:
  // - systems tab (list)
  // - split tabs (they query systems)
  // - system-scoped action-center selection (used to decide routing/focus)
  const shouldLoadSystems = canViewSystemList && (isSystemsTabActive || isSplitTabActive || selectionIsSystemScope);

  const { items: systemsItems, loading: systemsLoading } = useSystems({ enabled: shouldLoadSystems });
  const ownedSystems = useMemo(() => {
    if (!canViewSystemList || !systemsItems) return [];
    return systemsItems
      .filter((system) => system.owner_id === user?.id)
      .map((system) => ({ id: system.id }));
  }, [systemsItems, user?.id, canViewSystemList]);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedActionItemId(null);
  };

  useEffect(() => {
    // fetch tabs
    const tabFetcher = async () => {
      const availableTabs: BudgetTabItem[] = [
        {
          id: 'global',
          label: 'Global Budget',
          component: <GlobalBudget />,
          hasAccess: hasUserAccessTo(PermissionArea.GLOBAL_BUDGET),
          dataIntro:
            'In Global Budget Section you can set up a monthly value for all of your cloud spending. If this budget will be breached, the CFO will be informed. You can also see how your cloud spending looked like in the last 12 months, against the global budget value.',
        },
        {
          id: 'global-budget-split',
          label: 'Global Budget Split',
          component: <GlobalBudgetSplit />,
          hasAccess: hasUserAccessTo(PermissionArea.DEPARTMENT_BUDGET_VIEW_ALL),
          dataIntro: '',
        },
        {
          id: 'departments',
          label: 'Departments',
          component: <DepartmentsBudget />,
          hasAccess:
            user?.is_admin ||
            user?.is_sponsor ||
            (await checkSpecialPermission(
              PermissionArea.SPECIAL_IS_DEPARTMENT_OWNER,
            )),
          dataIntro:
            'This Tab allow You to define Departments in the application. Departmen needs to have an owner who is responsible for all the costs of the department. Later, you can assign users to specific departments, and department cost will be a sum of all user costs in that department.',
        },
        {
          id: 'department-budget-split',
          label: 'Department Budget Split',
          component: <DepartmentBudgetSplit />,
          hasAccess:
            (await checkSpecialPermission(
              PermissionArea.SPECIAL_IS_DEPARTMENT_OWNER,
            )) || hasUserAccessTo(PermissionArea.GLOBAL_BUDGET),
          dataIntro: '',
        },
        {
          id: 'systems',
          label: 'Owned Systems',
          component: <SystemsBudget />,
          hasAccess: await checkSpecialPermission(
            PermissionArea.SPECIAL_IS_SYSTEM_OWNER,
          ),
          dataIntro:
            'In this section you can defined Systems and their budget. A System can represent a project, an application, or any other business unit. You can then give it a budget, and we will inform you if it will be breached in any given month. Systems need to have an owner as there always have to be a person responsible that is informed and accountable for the breach ',
        },
        {
          id: 'approval',
          label: 'Budget Change Approval',
          component: <BudgetChangeApproval />,
          hasAccess:
            user?.is_admin ||
            user?.is_sponsor ||
            (await checkSpecialPermission(
              PermissionArea.SPECIAL_IS_DEPARTMENT_OWNER,
            )) ||
            (await checkSpecialPermission(
              PermissionArea.SPECIAL_IS_SYSTEM_OWNER,
            )),
          dataIntro:
            'In this Section you Approve requests to change budgets for Systems, Users or Departments. Untill the budget is approved here, the old value of the budget will be in place',
        },
      ];

      setAvailableTabs(availableTabs.filter((tab) => tab.hasAccess));
      setHasFetchedTabs(true);
    };
    tabFetcher();
  }, [user?.is_admin, user?.is_sponsor]);

  // Deep link: Budget sub-tab selection by scope and user role
  const setTabById = useCallback(
    (tabId: string) => {
      const idx = availableTabs.findIndex((tab) => tab.id === tabId);
      if (idx !== -1) {
        setValue(idx);
      }
    },
    [availableTabs],
  );

  // Fallback handler: switch to departments tab if system not found in owned systems
  const handleSystemNotFound = useCallback(
    (systemId: number, departmentId?: number) => {
      setTabById('departments');
      if (departmentId) {
        setFocusDepartmentId(departmentId);
      }
      if (systemId) {
        setFocusSystemId(systemId);
      }
    },
    [setTabById],
  );
  useActionCenterViewController({
    module: 'Budgets',
    items: actionCenterItems,
    enabled: hasFetchedTabs && availableTabs.length > 0,
    handler: createBudgetsActionCenterHandler({
      userId: user?.id,
      setSelectedItemId: setSelectedActionItemId,
      setIsDialogOpen,
      setFocusDepartmentId,
      setFocusSystemId,
      setTabById,
      ownedSystems,
      systemsLoading,
    }),
  });

  // Don't clear selection when tabs are loading - let the handler process it once tabs are ready
  // Only clear if we've fetched tabs and there are none available (user has no access)
  useEffect(() => {
    if (hasFetchedTabs && availableTabs.length === 0) {
      clearSelection();
    }
  }, [hasFetchedTabs, availableTabs.length, clearSelection]);

  // Scroll to relevant section when action item is clicked (only when item is selected, not when cleared)
  useEffect(() => {
    if (!selectedActionItemId || !isDialogOpen) return;

    const currentTab = availableTabs[value];
    if (!currentTab) return;

    // Wait for tab to be fully rendered before scrolling (especially when navigating from dashboard)
    const scrollTimeout = setTimeout(() => {
      if (currentTab.id === 'departments') {
        if (focusSystemId) {
          scrollSectionIntoView('[data-systems-section]', { delayMs: 600 });
          return;
        }

        if (focusDepartmentId) {
          scrollSectionIntoView('[data-department-section]', { delayMs: 600 });
        }
        return;
      }

      if (currentTab.id === 'systems' && focusSystemId) {
        scrollSectionIntoView('[data-system-section]', { delayMs: 600 });
      }
    }, 100);

    return () => clearTimeout(scrollTimeout);
  }, [
    selectedActionItemId,
    isDialogOpen,
    value,
    availableTabs,
    focusDepartmentId,
    focusSystemId,
    v2Item,
  ]);

  const handleChange = (_event: any, newValue: any) => {
    setValue(newValue);
    // Clear selection when user manually changes tabs
    clearSelection();
    setFocusDepartmentId(undefined);
    setFocusSystemId(undefined);
  };

  if (!getToken()) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return (
    <CurrencyProvider>
      <AppContainer>

        <ActionCenterV2
          endpointUrl={urls.DevOps.getBudgetActionCenterV2}
          onItemsLoaded={setActionCenterItems}
        />

        <ActionItemTextDialog
          item={v2Item}
          currentUserId={user?.id}
          open={isDialogOpen}
          onClose={handleDialogClose}
        />

        {hasFetchedTabs && availableTabs.length === 0 ? (
          <div className="card" style={{ height: '100%', width: '100%' }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#8A94A6', fontSize: 14, p: 4 }}>
              Budget data not available fot your user role
            </Box>
          </div>
        ) : (
          <>
            <BudgetStatsRow />
            <div className="card" style={{ height: '100%', width: '100%' }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile>
                  {availableTabs.map((tab, index) => (
                    <Tab
                      key={tab.id}
                      label={tab.label}
                      {...a11yProps(index)}
                      data-intro={tab.dataIntro}
                      data-testid={`tab-${tab.id}`}
                    />
                  ))}
                </Tabs>
              </Box>

              {availableTabs.map((tab, index) => {
                const extras = {
                  focusDepartmentId,
                  focusSystemId,
                  onSystemNotFound: handleSystemNotFound,
                  isActive: value === index, // Pass whether this tab is currently active
                };
                return (
                  <TabPanel key={tab.id} value={value} index={index}>
                    {renderTabContent(tab, extras)}
                  </TabPanel>
                );
              })}
            </Box>
            </div>
          </>
        )}
      </AppContainer>
    </CurrencyProvider>
  );
}

export default Budgets;

function renderTabContent(
  tab: BudgetTabItem,
  extras?: {
    focusDepartmentId?: number;
    focusSystemId?: number;
    onSystemNotFound?: (systemId: number, departmentId?: number) => void;
    isActive?: boolean;
  },
) {
  if (tab.id === 'departments') {
    const props = {
      key: `dept-${extras?.focusDepartmentId || 'none'}-sys-${extras?.focusSystemId || 'none'}`,
      initialDepartmentId: extras?.focusDepartmentId,
      initialSystemId: extras?.focusSystemId,
    };
    return React.cloneElement(tab.component as React.ReactElement<any>, props);
  }

  if (tab.id === 'systems') {
    const props = {
      key: `sys-${extras?.focusSystemId || 'none'}`,
      initialSystemId: extras?.focusSystemId,
      onSystemNotFound: extras?.onSystemNotFound,
      isActive: extras?.isActive ?? true,
    };
    return React.cloneElement(tab.component as React.ReactElement<any>, props);
  }

  return tab.component;
}
